/**
 * The narrowing roster AS DATA (R15) — which canonical intent tokens can
 * produce which runtime actions, and which tokens the roster answers at all.
 *
 * @remarks
 * The occurrence-producibility check ("no trigger listens for an occurrence
 * this pack cannot produce" — the D31 law one level deeper, R15) needs to ask
 * a question only narrowing can answer: given a compiled vocabulary, can any
 * typed sentence ever EXECUTE as action X? The narrowing switch knows, but a
 * `switch` cannot be queried — so its verb knowledge is stated here as data,
 * exactly as `RUNTIME_ACTION_TYPES` states the union's discriminants.
 *
 * DRY honesty: this table is a SECOND STATEMENT of facts the roster and the
 * interceptors already encode in code, and a second statement can drift. The
 * drift guard is `intent-action-map.test.ts`, which drives
 * `narrowParsedCommand` itself for every mapping and fails the moment the
 * table and the behavior disagree — the same pin the `Record satisfies`
 * lists get from the compiler, obtained from the suite because these facts
 * live in control flow rather than in a type.
 *
 * `go` deserves its footnote: bare direction shorthands ("north") parse
 * through the lexicon's built-in function words whatever verbs a pack
 * declares, so movement needs no typed verb and its entry is the empty list —
 * read it as "no vocabulary requirement", never as "unreachable".
 *
 * @packageDocumentation
 */

import type { RuntimeAction } from './runtime-action.js';

import { CHROME_DIRECTIVE_TOKENS, WITHDRAWAL_INTENT_TOKEN } from '../execution/chrome-directive-tokens.js';

/**
 * For each runtime action, every canonical intent token whose narrowing can
 * mint it (interceptors included — `turn` reaches the switch actions, `use`
 * with a hands instrument reaches `open_container`). Empty means "no typed
 * verb required" (movement's direction shorthands).
 */
export const INTENTS_PRODUCING_ACTION = {
	// D196: an authored verb is whichever supported token the roster does NOT
	// answer — a pack's own word, never a fixed one — so, like `go`, the list is
	// empty: read it as "any declared verb outside this table", never as
	// "unreachable". The producibility table judges it against the pack's
	// `verbInteractions` and its merged vocabulary together.
	act: [],
	// D137/D231: `ask about the room` is an `ask` whose subject is the room.
	ask: ['ask'],
	// D234: the strike — the actor intercept reads `attack` aimed at a foe. On a THING the same verb refuses in D47's shape.
	attack: ['attack'],
	close_container: ['close'],
	drop: ['drop'],
	// D143: the wear family — `wear`, `put on`, `don` are aliases of `equip`.
	equip: ['equip'],
	// D235: a thing handed to a body — the actor intercept reads `give` with a body after `to`.
	give: ['give'],
	go: [],
	help: ['help'],
	inspect: ['inspect'],
	// D232: looking at an actor — the actor intercept reads `inspect`/`look at` aimed at a body.
	inspect_actor: ['inspect'],
	// D217: looking at a set a plural named (`examine open jars`), under the pack's `set` policy.
	inspect_set: ['inspect'],
	inventory: ['inventory'],
	// The room-aimed interceptor (D41) reads `inspect the room` as LOOK.
	look: ['inspect', 'look'],
	map: ['map'],
	// `open` hand-opens directly; `use`/`open` with a hands instrument (D63)
	// reach the same door.
	open_container: ['open', 'use'],
	// D252: an order is whatever clause was said to a body — any declared verb,
	// never a fixed one — so, like `act` and `go`, the list is empty. The
	// producibility table judges it against the pack's `orders` tables.
	order: [],
	search: ['search'],
	// D240: going through a body that carries something — the actor intercept reads `search` aimed at it.
	search_actor: ['search'],
	search_room: ['search'],
	// D235: a thing shown to a body — the same table as `give`, the thing kept.
	show: ['show'],
	switch_off: ['turn'],
	switch_on: ['turn'],
	// D151: `remove` is unequip when the thing is worn and take otherwise —
	// one verb, two actions, the target's state decides (Calder's finding:
	// `remove the mask` used to take the mask off your own face as a pick-up).
	take: ['remove', 'take'],
	unequip: ['remove', 'unequip'],
	// Both spellings of the one action (R2, "open vs use"): `use X on Y` and
	// `open Y with X` land on `use`.
	use: ['open', 'use'],
	wait: ['wait'],
} as const satisfies Record<RuntimeAction['type'], readonly string[]>;

/**
 * Every canonical intent token the narrowing roster answers — the ordinary
 * verb cases plus the interceptors' `turn`. A SUPPORTED compiled intent
 * outside this list falls through to the roster's default arm and earns
 * `unknown_intent`, which is how the producibility table decides whether that
 * rejection reason is reachable in a given vocabulary.
 */
export const NARROWABLE_INTENT_TOKENS: readonly string[] = [
	...new Set(Object.values(INTENTS_PRODUCING_ACTION).flat()),
].sort();

/**
 * D196: every canonical token the ENGINE claims — the roster's verbs plus
 * the whole-input directives read before any parse (SAVE, LOAD, RESTART, and
 * the question withdrawal). An authored verb (`verbInteractions`) may be any
 * declared, supported token outside this list, and the loader refuses a key
 * inside it: a pack cannot re-author what the engine already does, and a
 * table keyed on `take` or `save` would be a beat that fires for nobody.
 */
export const ENGINE_CLAIMED_INTENT_TOKENS: readonly string[] = [
	...new Set([...NARROWABLE_INTENT_TOKENS, ...Object.values(CHROME_DIRECTIVE_TOKENS), WITHDRAWAL_INTENT_TOKEN]),
].sort();
